import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useWebsiteConfigQuery } from "hooks/useWebsiteConfig";

// ─── Skeleton shimmer ────────────────────────────────────────────────────────
const shimmer = keyframes`
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
`;

const Skeleton = styled.div`
  border-radius: ${({ radius }) => radius || "6px"};
  height: ${({ h }) => h || "14px"};
  width: ${({ w }) => w || "100%"};
  background: linear-gradient(90deg, #2a2e30 25%, #343a3c 50%, #2a2e30 75%);
  background-size: 600px 100%;
  animation: ${shimmer} 1.4s infinite linear;
`;

// ─── Styled components ────────────────────────────────────────────────────────
const Container = styled.div`
  color: #f5f6f7;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
`;

const PageTitle = styled.h2`
  font-size: 24px;
  font-weight: 800;
  color: #ffffff;
  margin: 0 0 20px 0;
`;

const CategoriesContainer = styled.div`
  display: inline-flex;
  background: #292d2e;
  border-radius: 9999px;
  padding: 4px;
  margin-bottom: 20px;
  overflow-x: auto;
  max-width: 100%;
  border: 1px solid #3a4142;
  &::-webkit-scrollbar { display: none; }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const CategoryButton = styled.button`
  padding: 8px 24px;
  border-radius: 9999px;
  border: none;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  background: ${props => props.active ? "#3a4142" : "transparent"};
  color: ${props => props.active ? "#ffffff" : "#b1b6bb"};
  transition: all 0.2s;
  &:hover { color: #ffffff; }
`;

const AccordionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AccordionItem = styled.div`
  background: #292d2e;
  border: 1px solid #3a4142;
  border-radius: 6px;
  overflow: hidden;
`;

const AccordionTrigger = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: transparent;
  border: none;
  color: #ffffff;
  font-weight: 600;
  font-size: 14.5px;
  text-align: left;
  cursor: pointer;
  &:hover { background: rgba(58, 65, 66, 0.2); }
`;

const AccordionContent = styled.div`
  padding: 0 16px 16px 16px;
  font-size: 13.5px;
  color: #b1b6bb;
  line-height: 1.6;
  white-space: pre-line;
`;

const SkeletonTabsRow = styled.div`
  display: inline-flex;
  gap: 8px;
  background: #292d2e;
  border-radius: 9999px;
  padding: 4px 8px;
  margin-bottom: 20px;
  border: 1px solid #3a4142;
`;

const SkeletonAccordionItem = styled.div`
  background: #292d2e;
  border: 1px solid #3a4142;
  border-radius: 6px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

// ─── Component ───────────────────────────────────────────────────────────────
const Faq = () => {
  const [category, setCategory] = useState("General");
  const [openIndex, setOpenIndex] = useState(null);
  const { data: config, isLoading } = useWebsiteConfigQuery();

  // Parse FAQs from backend — no static fallback
  const raw = config?.affiliate_faq;
  const allFaqs = raw
    ? (typeof raw === "string" ? JSON.parse(raw) : raw)
    : [];

  // Derive unique category tabs from actual data
  const categories = Array.isArray(allFaqs) && allFaqs.length > 0
    ? [...new Set(allFaqs.map(f => f.category || "General"))]
    : ["General", "Affiliate Program", "Earnings"];

  const categoryFaqs = Array.isArray(allFaqs)
    ? allFaqs.filter(f => (f.category || "General") === category)
    : [];

  return (
    <Container>
      <PageTitle>FAQ</PageTitle>

      {/* ── Category tabs ── */}
      {isLoading ? (
        <SkeletonTabsRow>
          <Skeleton w="80px" h="34px" radius="9999px" />
          <Skeleton w="130px" h="34px" radius="9999px" />
          <Skeleton w="90px" h="34px" radius="9999px" />
        </SkeletonTabsRow>
      ) : (
        <CategoriesContainer>
          {categories.map(c => (
            <CategoryButton
              key={c}
              active={category === c}
              onClick={() => { setCategory(c); setOpenIndex(null); }}
            >
              {c}
            </CategoryButton>
          ))}
        </CategoriesContainer>
      )}

      {/* ── Accordion list ── */}
      <AccordionList>
        {isLoading
          ? [0, 1, 2, 3].map(i => (
              <SkeletonAccordionItem key={i}>
                <Skeleton w={`${55 + i * 8}%`} h="14px" />
                <Skeleton w="18px" h="18px" radius="4px" style={{ flexShrink: 0, marginLeft: 12 }} />
              </SkeletonAccordionItem>
            ))
          : categoryFaqs.length === 0
          ? (
              <AccordionItem>
                <AccordionTrigger as="div" style={{ cursor: "default", color: "#b1b6bb" }}>
                  No FAQs available for this category yet.
                </AccordionTrigger>
              </AccordionItem>
            )
          : categoryFaqs.map((faq, i) => {
              const isOpen = openIndex === i;
              return (
                <AccordionItem key={i}>
                  <AccordionTrigger onClick={() => setOpenIndex(isOpen ? null : i)}>
                    <span>{faq.question}</span>
                    {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </AccordionTrigger>
                  {isOpen && <AccordionContent>{faq.answer}</AccordionContent>}
                </AccordionItem>
              );
            })
        }
      </AccordionList>
    </Container>
  );
};

export default Faq;
